<template>
  <div style="padding-bottom: 20px">
    <div v-if="error">{{ error }}</div>
    <div v-else style="max-width: 600px; margin: auto">
      <div v-if="trip">
        <v-card-title> Trip #{{ tripId }} Payments </v-card-title>
        <v-card-subtitle class="text-subtitle-1">
          <span style="margin-right: 10px">{{ toFromString(trip) }}</span>
          <span style="padding-top: 3px">{{
            getDatePortion(trip.fromTime)
          }}</span>
          <div>
            <v-icon class="mb-1">mdi-ferry</v-icon>
            <span class="subtitle-1">{{ trip.boat.name }}</span>
          </div>
          <span v-if="trip.route" class="subtitle-1">
            <v-icon>mdi-map-marker-path</v-icon>{{ trip.route.name }}</span
          >
          <div v-else>
            <v-icon dense class="mb-1">mdi-map-marker</v-icon>
            <span class="subtitle-1">{{ trip.fromLocation.name }}</span>
            <v-icon class="mb-1">mdi-ray-start-arrow</v-icon>
            <span class="subtitle-1">{{ trip.toLocation.name }}</span>
          </div>
          <div>
            <v-icon dense>mdi-account</v-icon>
            {{ trip.booked }}/{{ trip.capacity }}
            seats booked
            <SeatBreakdown :bookings="trip.bookings" />
          </div>
          <FuelConsumption
            v-if="trip.fuelConsumptionLitres"
            :trip="trip"
            :hideButton="true"
          />
        </v-card-subtitle>
      </div>
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-simple-table v-if="report" style="white-space: nowrap">
        <template v-slot:default>
          <tbody>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Free of charge
                  <SeatBreakdown
                    :bookings="filterBookings('', 'Free of charge')"
                  />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('', 'Free of charge')"
                />
              </td>
              <td></td>
            </tr>
            <tr
              v-if="
                bmlGatewayAllowed ||
                report.mvrtransaction > 0 ||
                report.usdtransaction > 0
              "
            >
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Card
                  <SeatBreakdown :bookings="filterBookings('MVR', 'Card')" />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('MVR', 'Card')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.mvrtransaction) }} MVR
              </td>
            </tr>
            <tr
              v-if="
                bmlGatewayAllowed ||
                report.mvrtransaction > 0 ||
                report.usdtransaction > 0
              "
            >
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Card
                  <SeatBreakdown :bookings="filterBookings('USD', 'Card')" />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('USD', 'Card')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.usdtransaction) }} USD
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Transfer
                  <SeatBreakdown
                    :bookings="filterBookings('MVR', 'Transfer')"
                  />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('MVR', 'Transfer')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.mvrtransfer) }} MVR
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Transfer
                  <SeatBreakdown
                    :bookings="filterBookings('USD', 'Transfer')"
                  />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('USD', 'Transfer')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.usdtransfer) }} USD
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Cash
                  <SeatBreakdown :bookings="filterBookings('MVR', 'Cash')" />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('MVR', 'Cash')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.mvrcash) }} MVR
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Cash
                  <SeatBreakdown :bookings="filterBookings('USD', 'Cash')" />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('USD', 'Cash')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.usdcash) }} USD
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Credit
                  <SeatBreakdown :bookings="filterBookings('MVR', 'Credit')" />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('MVR', 'Credit')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.mvrcredit) }} MVR
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Credit
                  <SeatBreakdown :bookings="filterBookings('USD', 'Credit')" />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('USD', 'Credit')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.usdcredit) }} USD
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Pre-paid
                  <SeatBreakdown
                    :bookings="filterBookings('MVR', 'Pre-paid')"
                  />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('MVR', 'Pre-paid')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.mvrprepaid) }} MVR
              </td>
            </tr>
            <tr>
              <td style="width: 100%">
                <div style="padding-top: 0.5rem">
                  Pre-paid
                  <SeatBreakdown
                    :bookings="filterBookings('USD', 'Pre-paid')"
                  />
                </div>
                <TripPaymentBookings
                  :bookings="filterBookings('USD', 'Pre-paid')"
                />
              </td>
              <td style="text-align: right">
                {{ decimalCurrency(report.usdprepaid) }} USD
              </td>
            </tr>
            <tr class="font-weight-bold">
              <td style="width: 100%">Total</td>
              <td style="text-align: right">
                <div>{{ decimalCurrency(report.mvrtotal) }} MVR</div>
                <div>{{ decimalCurrency(report.usdtotal) }} USD</div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import SeatBreakdown from "../components/Booking/SeatBreakdown.vue";
import FuelConsumption from "../components/Trip/FuelConsumption.vue";
import TripPaymentBookings from "../components/Trip/TripPaymentBookings.vue";
import {
  apiRequest,
  decimalCurrency,
  filterBookings,
  getDatePortion,
  toFromString,
} from "../helpers";

export default {
  name: "TripPayments",
  props: {
    tripId: String,
  },
  components: { SeatBreakdown, TripPaymentBookings, FuelConsumption },
  data: () => ({
    dialog: null,
    report: null,
    loading: false,
    error: "",
    trip: null,
    tripLoading: false,
  }),
  computed: {
    bmlGatewayAllowed() {
      return process.env.VUE_APP_ALLOW_BML_GATEWAY === "true" ? true : false;
    },
  },
  methods: {
    filterBookings(currency, type) {
      return filterBookings(currency, this.trip.bookings, type, true);
    },
    toFromString(trip) {
      return toFromString(trip, this.$store.getters.getConfigs);
    },
    getDatePortion(x) {
      return getDatePortion(x);
    },
    decimalCurrency(number) {
      return decimalCurrency(number);
    },
    getTrip() {
      const endpoint = `/api/gettrip?id=${this.tripId}`;
      const thenHandle = (resp) => {
        this.trip = resp.data.trip ?? null;
      };
      const catchHandle = (error) => {
        if (error.response) {
          this.error = error.response.data.error;
        }
      };
      const finallyHandle = () => {
        this.tripLoading = false;
        this.getPaymentDetails();
      };
      this.tripLoading = true;
      apiRequest({
        endpoint,
        methods: "GET",
        thenHandle,
        catchHandle,
        finallyHandle,
      });
    },
    getPaymentDetails() {
      this.loading = true;
      const query = `?tripId=${this.tripId}`;
      axios({
        url: `${
          process.env.VUE_APP_BASE_URL ?? ""
        }/api/trippaymentreport${query}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.report = resp.data.report;
        })
        .catch((error) => {
          if (error.response) {
            this.error = error.response.data.error;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getConfigs() {
      const endpoint = "/api/getconfigs";
      const thenHandle = (resp) => {
        const configs = resp?.data?.configs;
        if (configs) {
          this.configs = configs;
          this.$store.dispatch("configure", {
            configs,
          });
        }
      };
      apiRequest({
        endpoint,
        method: "GET",
        thenHandle,
      });
    },
  },
  mounted() {
    document.title = `Trip #${this.tripId} Payments`;
    this.getConfigs();
    this.getTrip();
  },
};
</script>
